/**
 * Vincent B940 (Eric 2026-02-14 SPEC STRICK PROD) — Page admin
 * de fusion des variantes de locuteurs à travers TOUTES les vidéos
 * de l'organisation (Cross-Video Speaker Merge).
 *
 * Flow :
 *   1. Charge les groupes de variantes détectées via
 *      `GET /api/admin/speakers/variants` (normalisation NFD/whitespace/case).
 *   2. Pour chaque groupe (≥2 variantes distinctes), Vincent choisit
 *      le nom canonique (défaut : la variante avec le plus de vidéos).
 *   3. `POST /api/admin/speakers/normalize { names, canonical }` renomme
 *      toutes les occurrences dans transcript_segments + Fireflies + Fusion.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SpeakerVariantsAdminPage() {
  const { tr } = useLang();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null); // key du groupe en cours
  const [dismissed, setDismissed] = useState(new Set());
  const [canonicalByKey, setCanonicalByKey] = useState({}); // key → nom choisi
  const [lastReport, setLastReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/speakers/variants`,
        { withCredentials: true },
      );
      const list = data?.groups || [];
      setGroups(list);
      // Défaut : nom canonique = variante avec le plus de vidéos (index 0)
      const defaults = {};
      list.forEach((g) => {
        defaults[g.key] = g.variants?.[0]?.name || "";
      });
      setCanonicalByKey(defaults);
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doNormalize = async (group) => {
    if (merging) return;
    const canonical = (canonicalByKey[group.key] || "").trim();
    if (!canonical) {
      toast.error(tr("Sélectionne un nom canonique avant de fusionner.", "Pick a canonical name before merging."));
      return;
    }
    const names = (group.variants || []).map((v) => v.name);
    if (names.length < 2) {
      toast.error(tr("Il faut au moins 2 variantes.", "At least 2 variants are required."));
      return;
    }
    setMerging(group.key);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/speakers/normalize`,
        { names, canonical },
        { withCredentials: true },
      );
      toast.success(
        `« ${canonical} » — ${data?.videos_affected || 0} vidéo(s), ${data?.total_segments_renamed || 0} segment(s) renommé(s)`,
      );
      setLastReport(data);
      // Retire le groupe de la liste (résolu)
      setGroups((prev) => prev.filter((g) => g.key !== group.key));
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setMerging(null);
    }
  };

  const dismissGroup = (key) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const visibleGroups = groups.filter((g) => !dismissed.has(g.key));

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="speaker-variants-admin-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">
            {tr("🎙️ Fusion de locuteurs (multi-vidéos)", "🎙️ Speaker merge (across videos)")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            {tr(
              "Détecte les variantes du même locuteur à travers toutes les vidéos de l'organisation (accents, casse, espaces). Choisis le nom canonique et fusionne d'un clic sur toute la base.",
              "Detects variants of the same speaker across every video of the organisation (accents, case, spaces). Pick the canonical name and merge library-wide in one click.",
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="speaker-variants-refresh-btn"
        >
          {loading ? tr("⏳ Chargement…", "⏳ Loading…") : tr("↻ Recharger", "↻ Reload")}
        </button>
      </div>

      <div className="mb-4 bg-zinc-50 border border-zinc-200 rounded p-3">
        <p className="text-xs text-zinc-600">
          <strong>{visibleGroups.length}</strong>{" "}
          {tr("groupe(s) de variantes détecté(s). Seuls les groupes avec ≥2 variantes distinctes sont listés.",
              "variant group(s) detected. Only groups with 2 or more distinct variants are listed.")}
        </p>
      </div>

      {lastReport && (
        <div
          className="mb-4 bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900"
          data-testid="speaker-variants-last-report"
        >
          <p className="font-semibold mb-1">
            ✓ Dernière fusion : « {lastReport.canonical} »
          </p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Variantes fusionnées : {(lastReport.normalized_from || []).join(", ")}</li>
            <li>Vidéos impactées : {lastReport.videos_affected}</li>
            <li>Segments renommés : {lastReport.total_segments_renamed}</li>
            {lastReport.stats && (
              <li>
                Détails : Whisper {lastReport.stats.whisper_segments} +
                Fireflies {lastReport.stats.fireflies_segments} +
                Fusion {lastReport.stats.merged_segments}
              </li>
            )}
          </ul>
        </div>
      )}

      {visibleGroups.length === 0 && !loading ? (
        <p
          className="text-sm text-zinc-500 italic bg-white border border-zinc-200 rounded p-6 text-center"
          data-testid="speaker-variants-empty"
        >
          ✓ Aucune variante de locuteur détectée dans l&apos;organisation.
        </p>
      ) : (
        <ul className="space-y-3" data-testid="speaker-variants-list">
          {visibleGroups.map((g) => {
            const chosen = canonicalByKey[g.key] || "";
            const isMerging = merging === g.key;
            return (
              <li
                key={g.key}
                className="bg-white border border-zinc-200 rounded p-4"
                data-testid={`speaker-variant-group-${g.key}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-zinc-500 mb-1">
                      clé normalisée : {g.key}
                    </p>
                    <p className="text-sm text-zinc-700">
                      <strong>{g.total_videos}</strong> vidéo(s) impactée(s) —{" "}
                      <strong>{g.variants.length}</strong> variante(s) détectée(s)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => dismissGroup(g.key)}
                    className="text-[11px] font-semibold px-2 py-1 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                    title={tr("Ignorer ce groupe pour cette session", "Skip this group for this session")}
                    data-testid={`speaker-variant-dismiss-btn-${g.key}`}
                  >
                    Ignorer
                  </button>
                </div>

                <div className="space-y-2 mb-3">
                  {g.variants.map((v) => (
                    <label
                      key={v.name}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                        chosen === v.name
                          ? "bg-teal-50 border-teal-300"
                          : "bg-zinc-50 border-zinc-200 hover:bg-zinc-100"
                      }`}
                      data-testid={`speaker-variant-choice-${g.key}-${v.name}`}
                    >
                      <input
                        type="radio"
                        name={`canonical-${g.key}`}
                        value={v.name}
                        checked={chosen === v.name}
                        onChange={() =>
                          setCanonicalByKey((prev) => ({ ...prev, [g.key]: v.name }))
                        }
                        className="accent-teal-700"
                        data-testid={`speaker-variant-radio-${g.key}-${v.name}`}
                      />
                      <span
                        className={`text-sm ${chosen === v.name ? "font-semibold text-teal-900" : "text-zinc-800"}`}
                      >
                        « {v.name} »
                      </span>
                      <span className="text-[11px] text-zinc-500 font-mono ml-auto">
                        {v.video_count} vidéo(s)
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={chosen}
                    onChange={(e) =>
                      setCanonicalByKey((prev) => ({ ...prev, [g.key]: e.target.value }))
                    }
                    placeholder={tr("Nom canonique (éditable)", "Canonical name (editable)")}
                    className="flex-1 text-sm px-2 py-1 border border-zinc-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid={`speaker-variant-canonical-input-${g.key}`}
                  />
                  <button
                    type="button"
                    onClick={() => doNormalize(g)}
                    disabled={isMerging || !chosen.trim()}
                    className="text-xs font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                    data-testid={`speaker-variant-merge-btn-${g.key}`}
                  >
                    {isMerging ? "⏳ Fusion…" : "▶ Fusionner KB-wide"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
